import { displayNameToOptionError } from 'services/helpers';
import {
  checkDeleted,
  checkListDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import { type ArchiveRecordStep } from '../actionSectionTypes';
import type { RecordSource } from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { RECORD_SOURCES } from './constants';
import type { Field } from 'pages/AutomationEngine/types';

const forApi = (step: ArchiveRecordStep) => {
  const details = { ...step.details };
  const { recordSource, relationshipFields, automationVariable } = details;

  const api = {
    details: {
      recordSource,
      relationshipFieldIds: relationshipFields.map(({ value }) => value),
      automationVariableName: automationVariable?.value || null,
    },
  };

  return api;
};

type ApiObject = {
  recordSource: RecordSource;
  relationshipFields: (Field & { deleted: boolean })[];
  automationVariable: {
    name: string;
    deleted: boolean;
    dataSubtype: string;
  } | null;
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const { recordSource, relationshipFields, automationVariable } = apiObject;

  if (recordSource === RECORD_SOURCES.AUTOMATION_VARIABLE) {
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      automationVariable,
      nameDescriptor,
      undefined,
      'automationVariable'
    );
  }

  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    checkListDeleted(
      messageDictionary,
      addErrorMessage,
      relationshipFields,
      displayNameDescriptor,
      messageDictionary.relatedObjectField,
      'relationshipFields',
      displayNameToOptionError,
      true
    );
  }

  let relatedObject = null;
  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    const firstField = relationshipFields[0];
    if (firstField) {
      const { relation } = firstField;
      relatedObject = {
        value: relation.relatedObject,
        label: relation.relatedObjectName,
        deleted: false,
        entityName: relation.relatedEntityName,
        relation,
      };
    }
  }

  return {
    recordSource,
    relatedObject,
    relationshipFields: (relationshipFields || []).map((field) => ({
      value: field.id,
      label: field.deleted
        ? `${field.displayName} ${messageDictionary.deleted}`
        : field.displayName,
      deleted: field.deleted,
      error: field.deleted,
      data: field,
    })),
    automationVariable: automationVariable && {
      value: automationVariable.name,
      label: automationVariable.deleted
        ? `${automationVariable.name} ${messageDictionary.deleted}`
        : automationVariable.name,
      deleted: automationVariable.deleted,
      meta: { dataSubtype: { value: automationVariable.dataSubtype } },
    },
  };
};

export const archiveRecordDTO = {
  forApi,
  forFlow,
};
